/**
 * StrategyModule - Strategic Framework Management v5.0.1
 *
 * REFACTORED: Now uses unified panel system for consistent UI
 * Features:
 * - Dual-panel layout: Upload Strategy | Build from Initiatives
 * - Drag & drop file upload
 * - Multiple format support (PDF, TXT, JSON, DOCX)
 * - AI-powered strategy extraction
 * - Emergent strategy from initiatives
 *
 * @module modules/strategy/StrategyModule
 */

import AppState from '../../core/AppState.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import StorageService from '../../services/StorageService.js';
import { actions as storeActions, dispatch as storeDispatch } from '../../store/index.js';
import InitiativeService from '../../services/InitiativeService.js';
import ValidationService from '../../services/ValidationService.js';
import ApiService from '../../services/ApiService.js';
import PromptService from '../../services/PromptService.js';
import { Toast } from '../../utils/ui.js';
import { bindPromptEditors } from '../../components/PromptBinder.js';
import { PromptEditor } from '../../components/PromptEditor.js';
import { bindSafeClick } from '../../utils/SafeClick.js';
import { confirmOverlay } from '../../utils/Overlay.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { installSplit } from '../../utils/Splitter.js';
import { createLayoutPanel, createPanelActions, PANEL_VARIANTS, PANEL_LAYOUTS } from '../../utils/panel.js';

export class StrategyModule {
  constructor() {
    this.container = null;
    this.uploadedStrategy = null;
    this.emergentStrategy = null;
    this.isProcessing = false;
    this.dragCounter = 0;
    
    // Load saved strategies
    this.loadStrategies();
  }
  
  async initialize() {
    console.log('📋 Strategy module initializing...');
    
    // Subscribe to strategy updates
    AppState.subscribe('strategies', (strategies) => {
      if (strategies) {
        this.uploadedStrategy = strategies.uploaded || null;
        this.emergentStrategy = strategies.emergent || null;
        if (this.container) {
          this.render();
        }
      }
    });
    
    // Listen for initiative updates to build emergent strategy
    EventBus.on(AppEvents.INITIATIVE_ADDED, () => {
      this.updateEmergentStrategy();
    });
    
    console.log('✅ Strategy module initialized');
  }
  
  mount(container) {
    this.container = container;
    this.render();
  }
  
  unmount() {
    if (this.container) {
      // Remove any active event listeners
      this.removeDropZoneListeners();
      this.container.innerHTML = '';
      this.container = null;
    }
  }
  
  activate() {
    console.log('📋 Strategy module activated');
    
    // Ensure module is properly mounted
    if (!this.container) {
      console.log('📋 Strategy module container not found, attempting to find and mount...');
      const strategyContainer = document.getElementById('strategy-section');
      if (strategyContainer) {
        this.mount(strategyContainer);
      } else {
        console.error('Strategy container #strategy-section not found in DOM');
        return;
      }
    }
    
    // Always re-render on activation to ensure fresh state
    this.render();
    
    console.log('✅ Strategy module activation complete');
  }
  
  loadStrategies() {
    // Load uploaded strategy
    const uploaded = StorageService.get('uploadedStrategy');
    if (uploaded) {
      this.uploadedStrategy = uploaded;
    }
    
    // Load emergent strategy
    const emergent = StorageService.get('emergentStrategy');
    if (emergent) {
      this.emergentStrategy = emergent;
    }
  }
  
  render() {
    if (!this.container) return;
    
    // Build uploaded strategy panel - explicitly layout-safe for split
    const uploadedActions = this.uploadedStrategy ? createPanelActions([
      { label: 'Clear and load', action: 'strategy-clear-uploaded', variant: 'ghost', size: 'small' }
    ]) : '';
    
    const uploadedPanel = createLayoutPanel({
      title: 'Uploaded Strategy',
      actions: uploadedActions,
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'strategy-panel upload-panel',
      content: this.uploadedStrategy ? 
        this.renderUploadedStrategy() : 
        this.renderUploadSection()
    });
    
    // Build emergent strategy panel - explicitly layout-safe for split
    const emergentActions = createPanelActions([
      { label: 'Build from Initiatives', action: 'strategy-build-emergent', variant: 'primary', size: 'small' },
      ...(this.emergentStrategy ? [
        { label: 'Clear', action: 'strategy-clear-emergent', variant: 'ghost', size: 'small' }
      ] : [])
    ]);
    
    const emergentPanel = createLayoutPanel({
      title: 'Emergent Strategy',
      actions: emergentActions,
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'strategy-panel initiatives-panel',
      content: this.emergentStrategy ? 
        this.renderEmergentStrategy() : 
        this.renderBuildSection()
    });
    
    this.container.innerHTML = `
      <div class="strategy-module">
        <div class="strategy-header">
          <div class="header-content">
            <h2>Strategic Framework</h2>
            <p class="strategy-subtitle">Upload your organization's strategy document or build one from initiatives</p>
          </div>
          
          <!-- Prompt Editors moved to right side with warning color -->
          <div class="prompt-editors-right">
            <button id="edit-upload-prompt" class="prompt-edit-btn" 
                    data-prompt-key="strategyFileUpload" 
                    data-prompt-name="Strategy File Upload">
              <span>Edit Upload AI Prompt</span>
            </button>
            <button id="edit-emergent-prompt" class="prompt-edit-btn"
                    data-prompt-key="emergentStrategy"
                    data-prompt-name="Emergent Strategy">
              <span>Edit Emergent Strategy AI Prompt</span>
            </button>
          </div>
        </div>
        
        <!-- Split Layout using panel system (grid + Splitter controls widths) -->
        <div class="strategy-dual-panel">
          ${uploadedPanel}
          ${emergentPanel}
        </div>
        
        <!-- Processing Overlay -->
        <div class="processing-overlay" id="processing-overlay" style="display: none;">
          <div class="processing-content">
            <div class="spinner-large"></div>
            <h3>Processing Strategy Document</h3>
            <p id="processing-status">Extracting text from document...</p>
            <div class="processing-steps">
              <div class="step" id="step-upload">Uploading file</div>
              <div class="step" id="step-extract">Extracting content</div>
              <div class="step" id="step-analyze">Analyzing with AI</div>
              <div class="step" id="step-structure">Structuring strategy</div>
              <div class="step" id="step-save">Saving results</div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Attach event handlers
    this.attachEventHandlers();

    // Install resizable splitter between strategy panels
    try { installSplit('.strategy-dual-panel', 'strategy'); } catch (_) {}

    // Mount Uploaded Summary island if available
    try {
      const summaryHost = this.container.querySelector('#uploaded-summary-island');
      if (summaryHost && this.uploadedStrategy) {
        Promise.all([
          import('../../ui/strategy/UploadedSummary.jsx'),
          import('preact')
        ]).then(([mod, preact]) => {
          const UploadedSummary = mod.UploadedSummary || mod.default;
          if (UploadedSummary && preact.render) preact.render(preact.h(UploadedSummary, { strategy: this.uploadedStrategy }), summaryHost);
        }).catch(()=>{});
      }
    } catch(_) {}
    
    // Bind PromptEditor buttons within this module's DOM
    try { bindPromptEditors(this.container); } catch {}
    // Production-safe direct binding to avoid any global delegation races
    try {
      bindSafeClick(this.container, '.prompt-edit-btn', (_e, btn) => {
        const key = btn.dataset.promptKey;
        const name = btn.dataset.promptName || key || 'Prompt';
        const editor = (window.promptEditor instanceof PromptEditor)
          ? window.promptEditor
          : new PromptEditor();
        window.promptEditor = editor;
        try { editor.open(key, name); } catch (err) { console.error('PromptEditor open failed:', err); }
      });
    } catch(_) {}
  }

  async handleBuildFromInitiatives() {
    // Delegate to the unified builder to avoid duplicate toasts/flows in DEV
    return this.buildEmergentStrategy();
  }
  
  renderUploadSection() {
    return `
      <div class="upload-drop-zone" id="strategy-drop-zone">
        <div class="drop-zone-content">
          <div class="drop-zone-icon"></div>
          <h4>Drag & drop your file here</h4>
          <p>or click to browse</p>
          <input 
            type="file" 
            id="strategy-file-input" 
            accept=".pdf,.txt,.json,.doc,.docx"
            hidden
          />
          <button class="btn-primary" id="browse-files" data-action="strategy-browse-files">
            <span class="btn-icon">📁</span> Choose File
          </button>
          <div class="supported-formats">
            Supported formats: PDF, TXT, JSON, DOC, DOCX (Max 10MB)
          </div>
        </div>
        <div class="drop-zone-active" style="display: none;">
          <div class="drop-zone-icon">⬇️</div>
          <p>Drop file here to upload</p>
        </div>
      </div>
    `;
  }
  
  renderUploadedStrategy() {
    const strategy = this.uploadedStrategy;
    
    return `
      <div class="strategy-display">
        ${strategy.metadata ? `
          <div class="strategy-metadata">
            <div class="metadata-item">
              <span class="metadata-label">Uploaded:</span>
              <span class="metadata-value">${new Date(strategy.metadata.uploadedAt).toLocaleDateString()}</span>
            </div>
          </div>
        ` : ''}
        
        <div class="strategy-content-display">
          ${this.renderStrategyContent(strategy)}
        </div>
        
        <div class="strategy-actions"></div>
      </div>
    `;
  }
  
  renderBuildSection() {
    const initiatives = (InitiativeService && InitiativeService.initialized)
      ? InitiativeService.getInitiatives()
      : (StorageService.get('initiatives', []));
    
    if (initiatives.length === 0) {
      return `
        <div class="empty-initiatives">
          <div class="empty-icon"></div>
          <h4>No Initiatives Yet</h4>
          <p>Create initiatives in the "Objectives & Initiatives" tab to build an emergent strategy.</p>
          <button class="btn-primary" id="go-to-initiatives" data-action="strategy-go-to-initiatives">Go to Initiatives</button>
        </div>
      `;
    }
    
    // No second build button – show short guidance only
    return `
      <div class="build-section" style="text-align:center;padding:1rem 0; color: var(--color-text-secondary)">
        Use the button in the header to generate an Emergent Strategy from your initiatives.
      </div>
    `;
  }
  
  renderEmergentStrategy() {
    const strategy = this.emergentStrategy;
    
    return `
      <div class="strategy-display">
        ${strategy.metadata ? `
          <div class="strategy-metadata">
            <div class="metadata-item">
              <span class="metadata-label">Generated:</span>
              <span class="metadata-value">${new Date(strategy.metadata.generatedAt).toLocaleDateString()}</span>
            </div>
            <div class="metadata-item">
              <span class="metadata-label">Based on:</span>
              <span class="metadata-value">${strategy.metadata.initiativeCount} initiatives</span>
            </div>
          </div>
        ` : ''}
        
        <div class="strategy-content-display">
          ${this.renderStrategyContent(strategy)}
        </div>
        
        <div class="strategy-actions">
          <button class="btn-primary btn-small" id="regenerate-emergent" data-action="strategy-regenerate-emergent">
            Regenerate
          </button>
        </div>
      </div>
    `;
  }
  
  renderStrategyContent(strategy) {
    if (!strategy) return '<p>No strategy content available</p>';
    
    // If strategy has structured content
    if (strategy.structured) {
      return `
        <div class="structured-strategy">
          ${strategy.structured.vision ? `
            <div class="strategy-section">
              <h4>Vision</h4>
              <p>${strategy.structured.vision}</p>
            </div>
          ` : ''}
          
          ${strategy.structured.mission ? `
            <div class="strategy-section">
              <h4>Mission</h4>
              <p>${strategy.structured.mission}</p>
            </div>
          ` : ''}
          
          ${strategy.structured.values && strategy.structured.values.length > 0 ? `
            <div class="strategy-section">
              <h4>Core Values</h4>
              <ul class="values-list">
                ${strategy.structured.values.map(value => `
                  <li>${value}</li>
                `).join('')}
              </ul>
            </div>
          ` : ''}
          
          ${strategy.structured.pillars && strategy.structured.pillars.length > 0 ? `
            <div class="strategy-section">
              <h4>Strategic Pillars</h4>
              <div class="pillars-grid">
                ${strategy.structured.pillars.map((pillar, index) => `
                  <div class="pillar-card">
                    <div class="pillar-number">${index + 1}</div>
                    <h5>${pillar.name}</h5>
                    <p class="pillar-desc">${pillar.description}</p>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
        </div>
      `;
    }
    
    // Fallback to raw content
    return `
      <div class="raw-strategy">
        <pre>${strategy.content || strategy.text || 'No content available'}</pre>
      </div>
    `;
  }
  
  attachEventHandlers() {
    // Upload handlers
    const dropZone = document.getElementById('strategy-drop-zone');
    const fileInput = document.getElementById('strategy-file-input');

    if (dropZone) {
      this.setupDropZone(dropZone, fileInput);
    }

    if (fileInput) {
      fileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    }

    // All primary actions are routed via ActionRouter (central registry or direct)
    this.registerRouterActions();

    // All actions are handled via ActionRegistry/ActionRouter; no fallbacks needed
  }

  registerRouterActions() {
    const mapping = {
      'strategy-browse-files': ({}) => {
        try { document.getElementById('strategy-file-input')?.click(); } catch (_) {}
      },
      'strategy-build-emergent': ({}) => this.buildEmergentStrategy(),
      'strategy-regenerate-emergent': ({}) => this.buildEmergentStrategy(),
      'strategy-go-to-initiatives': ({}) => EventBus.emit(AppEvents.TAB_CHANGED, 'objectives'),
      'strategy-clear-uploaded': { handler: () => this.clearStrategy('uploaded'), destructive: true },
      'strategy-clear-emergent': { handler: () => this.clearStrategy('emergent'), destructive: true }
    };
    ActionRegistry.registerMap('strategy', mapping);
  }
  
  setupDropZone(dropZone, fileInput) {
    // Drag and drop handlers
    dropZone.addEventListener('dragenter', (e) => this.handleDragEnter(e));
    dropZone.addEventListener('dragover', (e) => this.handleDragOver(e));
    dropZone.addEventListener('dragleave', (e) => this.handleDragLeave(e));
    dropZone.addEventListener('drop', (e) => this.handleDrop(e));
  }
  
  removeDropZoneListeners() {
    const dropZone = document.getElementById('strategy-drop-zone');
    if (dropZone) {
      dropZone.removeEventListener('dragenter', this.handleDragEnter);
      dropZone.removeEventListener('dragover', this.handleDragOver);
      dropZone.removeEventListener('dragleave', this.handleDragLeave);
      dropZone.removeEventListener('drop', this.handleDrop);
    }
  }
  
  handleDragEnter(e) {
    e.preventDefault();
    this.dragCounter++;
    const dropZone = document.getElementById('strategy-drop-zone');
    if (dropZone) {
      dropZone.classList.add('drag-over');
      dropZone.querySelector('.drop-zone-content').style.display = 'none';
      dropZone.querySelector('.drop-zone-active').style.display = 'flex';
    }
  }
  
  handleDragOver(e) {
    e.preventDefault();
  }
  
  handleDragLeave(e) {
    e.preventDefault();
    this.dragCounter--;
    if (this.dragCounter === 0) {
      const dropZone = document.getElementById('strategy-drop-zone');
      if (dropZone) {
        dropZone.classList.remove('drag-over');
        dropZone.querySelector('.drop-zone-content').style.display = 'flex';
        dropZone.querySelector('.drop-zone-active').style.display = 'none';
      }
    }
  }
  
  handleDrop(e) {
    e.preventDefault();
    this.dragCounter = 0;
    
    const dropZone = document.getElementById('strategy-drop-zone');
    if (dropZone) {
      dropZone.classList.remove('drag-over');
      dropZone.querySelector('.drop-zone-content').style.display = 'flex';
      dropZone.querySelector('.drop-zone-active').style.display = 'none';
    }
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      this.processFile(files[0]);
    }
  }
  
  handleFileSelect(e) {
    const files = e.target.files;
    if (files.length > 0) {
      this.processFile(files[0]);
    }
  }
  
  async processFile(file) {
    // Validate file
    const validation = ValidationService.validateFile(file);
    if (!validation.valid) {
      Toast.show(validation.errors.file || 'Invalid file', 'error');
      return;
    }
    
    // Show processing overlay
    this.showProcessing();
    this.updateProcessingStep('step-upload', 'active');
    
    try {
      // Read file content
      const content = await this.readFile(file);
      this.updateProcessingStep('step-upload', 'completed');
      this.updateProcessingStep('step-extract', 'active');
      
      // Extract text based on file type
      let extractedText = '';
      if (file.type === 'application/pdf') {
        // Note: In real implementation, would use PDF.js or similar
        extractedText = content; // Placeholder
        Toast.show('PDF extraction requires additional setup', 'warning');
      } else if (file.type === 'application/json') {
        try {
          const json = JSON.parse(content);
          extractedText = JSON.stringify(json, null, 2);
        } catch {
          extractedText = content;
        }
      } else {
        extractedText = content;
      }
      
      this.updateProcessingStep('step-extract', 'completed');
      this.updateProcessingStep('step-analyze', 'active');
      
      // Use AI to structure the strategy
      const structuredStrategy = await this.extractStrategyWithAI(extractedText, file.name);
      
      this.updateProcessingStep('step-analyze', 'completed');
      this.updateProcessingStep('step-structure', 'active');
      
      // Process and structure the strategy
      const strategy = {
        content: extractedText,
        structured: structuredStrategy,
        metadata: {
          filename: file.name,
          filesize: file.size,
          filetype: file.type,
          uploadedAt: new Date().toISOString(),
          extractedBy: 'AI Assistant'
        }
      };
      
      this.updateProcessingStep('step-structure', 'completed');
      this.updateProcessingStep('step-save', 'active');
      
      // Save strategy
      this.uploadedStrategy = strategy;
      StorageService.set('uploadedStrategy', strategy);
      try { storeDispatch(storeActions.setUploadedStrategy(strategy)); } catch (_) { AppState.setState({ strategies: { uploaded: strategy, emergent: this.emergentStrategy } }); }
      
      this.updateProcessingStep('step-save', 'completed');
      
      // Success
      Toast.show('Strategy uploaded and processed successfully', 'success');
      EventBus.emit('strategy:uploaded', strategy);
      
      // Hide processing and render view
      this.hideProcessing();
      this.render();
      
    } catch (error) {
      console.error('Error processing file:', error);
      Toast.show('Failed to process strategy file', 'error');
      this.hideProcessing();
    }
  }
  
  async readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }
  
  async extractStrategyWithAI(text, filename) {
    if (!ApiService.apiKey) {
      Toast.show('Please set your API key in Settings first', 'warning');
      return this.basicStrategyExtraction(text);
    }
    
    try {
      const prompt = PromptService.getPrompt('strategyFileUpload');
      const cfg = PromptService.getApiConfig('strategyFileUpload');
      const formattedPrompt = PromptService.formatPrompt('strategyFileUpload', {
        documentContent: text.substring(0, 8000), // Limit for API
        fileName: filename
      });
      
      const response = await ApiService.call(formattedPrompt, {
        maxTokens: 2000,
        temperature: 0.3,
        promptKey: 'strategyFileUpload',
        promptVersion: cfg.promptVersion
      });
      
      // Extract the content from the response
      const responseText = response.content || '';
      
      // Try to parse the response as structured data
      try {
        // Look for JSON in the response
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          return JSON.parse(jsonMatch[0]);
        }
      } catch {
        // If not JSON, parse as text sections
        return this.parseTextStrategy(responseText);
      }
      
      return responseText;
      
    } catch (error) {
      console.error('AI extraction error:', error);
      return this.basicStrategyExtraction(text);
    }
  }
  
  parseTextStrategy(text) {
    const strategy = {
      vision: '',
      mission: '',
      values: [],
      pillars: [],
      objectives: []
    };
    
    // Simple pattern matching for common strategy sections
    const visionMatch = text.match(/vision[:\s]+(.*?)(?=mission|values|$)/i);
    if (visionMatch) strategy.vision = visionMatch[1].trim();
    
    const missionMatch = text.match(/mission[:\s]+(.*?)(?=vision|values|$)/i);
    if (missionMatch) strategy.mission = missionMatch[1].trim();
    
    // Extract bullet points as objectives
    const bullets = text.match(/[•\-\*]\s+(.+)/g);
    if (bullets) {
      strategy.objectives = bullets.map(b => b.replace(/[•\-\*]\s+/, '').trim());
    }
    
    return strategy;
  }
  
  basicStrategyExtraction(text) {
    // Basic extraction without AI
    return {
      vision: 'To be extracted',
      mission: 'To be extracted',
      values: [],
      pillars: [],
      objectives: [],
      rawContent: text
    };
  }
  
  showProcessing() {
    const overlay = document.getElementById('processing-overlay');
    if (overlay) {
      overlay.style.display = 'flex';
      this.isProcessing = true;
    }
  }
  
  hideProcessing() {
    const overlay = document.getElementById('processing-overlay');
    if (overlay) {
      overlay.style.display = 'none';
      this.isProcessing = false;
    }
    
    // Reset all steps
    document.querySelectorAll('.step').forEach(step => {
      step.classList.remove('active', 'completed');
    });
  }
  
  updateProcessingStep(stepId, status) {
    const step = document.getElementById(stepId);
    if (step) {
      step.classList.remove('active', 'completed');
      if (status) {
        step.classList.add(status);
      }
    }
    
    // Update status text
    const statusText = document.getElementById('processing-status');
    if (statusText) {
      const messages = {
        'step-upload': 'Uploading file...',
        'step-extract': 'Extracting content...',
        'step-analyze': 'Analyzing with AI...',
        'step-structure': 'Structuring strategy...',
        'step-save': 'Saving results...'
      };
      
      if (status === 'active') {
        statusText.textContent = messages[stepId] || 'Processing...';
      }
    }
  }
  
  async buildEmergentStrategy() {
    // Prefer service for consistency
    let initiatives = [];
    try {
      const svc = (await import('../../services/InitiativeService.js')).default;
      if (!svc.initialized) { try { await svc.initialize(); } catch {} }
      initiatives = svc.getInitiatives();
    } catch {
      initiatives = StorageService.get('initiatives', []);
    }
    
    if (initiatives.length === 0) {
      Toast.show('No initiatives found. Create some initiatives first.', 'info');
      return;
    }
    
    Toast.show('Building emergent strategy from initiatives...', 'info');

    // Try AI-assisted pillar generation first (if API configured)
    let aiResult = null;
    try {
      if (ApiService?.apiKey || import.meta.env?.DEV) {
        const cfg = PromptService.getApiConfig('emergentStrategy');
        const items = initiatives.map(i => ({ id: i.id, title: i.title || '', objective: i.objective || '', description: i.description || '', strategicAlignment: i.strategicAlignment || '' })).slice(0, 200);
        const userPrompt = `From the following initiatives (JSON), infer the organization's strategic structure and return STRICT JSON with keys: vision (string), mission (string), values (array of strings), pillars (array of {name, description, goals: array of strings}).
Initiatives:\n${JSON.stringify(items).substring(0, 12000)}\n`;
        const resp = await ApiService.call(userPrompt, { temperature: 0.2, maxTokens: 1500, promptKey: 'emergentStrategy', promptVersion: cfg.promptVersion, response_format: { type: 'json_object' } });
        const text = resp?.content || '';
        const schema = await import('../../utils/schema.js');
        aiResult = schema.extractJSONObject(text);
      }
    } catch (e) {
      console.warn('AI emergent strategy failed, falling back:', e?.message || e);
    }

    // Fallback heuristic grouping if AI result missing/invalid
    const grouped = {};
    initiatives.forEach(init => {
      const alignment = init.strategicAlignment || 'Unaligned';
      if (!grouped[alignment]) grouped[alignment] = [];
      grouped[alignment].push(init);
    });

    const fallbackPillars = Object.keys(grouped).map(key => ({
      name: key,
      description: `Strategic focus area with ${grouped[key].length} initiatives`,
      goals: grouped[key].map(i => i.objective)
    }));

    const emergentStrategy = {
      structured: {
        vision: (aiResult && typeof aiResult.vision === 'string') ? aiResult.vision : 'Emergent vision based on organizational initiatives',
        mission: (aiResult && typeof aiResult.mission === 'string') ? aiResult.mission : 'To achieve excellence through coordinated strategic initiatives',
        values: Array.isArray(aiResult?.values) && aiResult.values.length ? aiResult.values : this.extractValuesFromInitiatives(initiatives),
        pillars: Array.isArray(aiResult?.pillars) && aiResult.pillars.length ? aiResult.pillars : fallbackPillars,
        objectives: initiatives.map(i => i.objective)
      },
      metadata: {
        generatedAt: new Date().toISOString(),
        source: aiResult ? 'AI Emergent Strategy' : 'Heuristic Emergent Strategy',
        initiativeCount: initiatives.length
      }
    };
    
    this.emergentStrategy = emergentStrategy;
    StorageService.set('emergentStrategy', emergentStrategy);
    try { storeDispatch(storeActions.setEmergentStrategy(emergentStrategy)); } catch (_) { AppState.setState({ strategies: { uploaded: this.uploadedStrategy, emergent: emergentStrategy } }); }
    
    Toast.show('Emergent strategy built successfully', 'success');
    this.render();
  }
  
  extractValuesFromInitiatives(initiatives) {
    // Extract common themes as values
    const themes = new Set();
    
    initiatives.forEach(init => {
      // Look for common keywords
      const text = `${init.title} ${init.description}`.toLowerCase();
      
      if (text.includes('innovat')) themes.add('Innovation');
      if (text.includes('customer') || text.includes('client')) themes.add('Customer Focus');
      if (text.includes('quality') || text.includes('excellen')) themes.add('Excellence');
      if (text.includes('collaborat') || text.includes('team')) themes.add('Collaboration');
      if (text.includes('sustain')) themes.add('Sustainability');
      if (text.includes('growth')) themes.add('Growth');
    });
    
    return Array.from(themes);
  }
  
  clearStrategy(type) {
    // Use lightweight overlay confirm to avoid modal flash issues
    confirmOverlay('Clear Strategy', `Are you sure you want to clear the ${type} strategy? This cannot be undone.`, () => {
        if (type === 'uploaded') {
          this.uploadedStrategy = null;
          StorageService.remove('uploadedStrategy');
        } else {
          this.emergentStrategy = null;
          StorageService.remove('emergentStrategy');
        }
        if (type === 'uploaded') {
          try { storeDispatch(storeActions.setUploadedStrategy(null)); } catch (_) { AppState.setState({ strategies: { uploaded: null, emergent: this.emergentStrategy } }); }
        } else {
          try { storeDispatch(storeActions.setEmergentStrategy(null)); } catch (_) { AppState.setState({ strategies: { uploaded: this.uploadedStrategy, emergent: null } }); }
        }
        
        Toast.show('Strategy cleared', 'info');
        this.render();
      }, () => {});
  }
  
  async updateEmergentStrategy() {
    // Auto-update emergent strategy when initiatives change
    if (this.emergentStrategy) {
      console.log('Updating emergent strategy based on initiative changes');
      // Could rebuild emergent strategy silently
    }
  }
}

// Export module - using named export to match import in App.js

// Attach to window in development
if (import.meta.env.DEV) {
  window.StrategyModule = StrategyModule;
  console.log('📋 StrategyModule v5.0.1 available at window.StrategyModule');
}
